/**
 * Redux slice for suppliers using consistent API response format
 * This will replace the legacy suppliersSlice.js
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import supplierService from '../services/supplierService';
import { Supplier, ApiResponse, PaginationInfo } from '../types/apiTypes';
import { handleApiError, parseConsistentApiResponse } from '../utils/consistentApiParser';

// Async thunks
export const getSuppliers = createAsyncThunk(
  'suppliers/getSuppliers',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = (getState() as any).suppliers;
      const now = Date.now();

      // Temporary logging to verify caching
      console.log('🔍 getSuppliers called with forceRefresh:', forceRefresh);
      console.log('📊 Suppliers cache status:', {
        cached: state.cached,
        lastFetched: state.lastFetched,
        age: state.lastFetched ? (now - state.lastFetched) / 1000 : 'never',
        timeout: state.cacheTimeout / 1000
      });

      // Check if we should use cached data
      if (!forceRefresh && state.cached && state.lastFetched &&
          (now - state.lastFetched) < state.cacheTimeout) {
        console.log('✅ Using cached suppliers data');
        // Return a special marker to indicate we're using cached data
        return {
          success: true,
          data: state.suppliers,
          pagination: null,
          message: '', // Empty string to avoid triggering success toast
          timestamp: new Date().toISOString(),
          fromCache: true // Special flag to identify cached responses
        } as ApiResponse<Supplier[]> & { fromCache: boolean };
      }

      console.log('🌐 Fetching fresh suppliers data');
      // Use the existing supplierService which already uses consistent API parsing
      const suppliers = await supplierService.getAll();

      // Wrap in consistent response format for consistency
      return {
        success: true,
        data: suppliers,
        pagination: null,
        message: `Retrieved ${suppliers.length} suppliers successfully`,
        timestamp: new Date().toISOString()
      } as ApiResponse<Supplier[]>;
    } catch (error) {
      console.error('suppliersSlice: Error fetching suppliers:', error);
      const errorMessage = handleApiError(error, 'consistentSuppliersSlice', 'getSuppliers');
      return rejectWithValue(errorMessage);
    }
  }
);

export const getSupplier = createAsyncThunk(
  'suppliers/getSupplier',
  async (id: string, { rejectWithValue }) => {
    try {
      const supplier = await supplierService.getById(id);

      return {
        success: true,
        data: supplier,
        pagination: null,
        message: 'Supplier retrieved successfully',
        timestamp: new Date().toISOString()
      } as ApiResponse<Supplier>;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentSuppliersSlice', 'getSupplier');
      return rejectWithValue(errorMessage);
    }
  }
);

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplierData: Partial<Supplier>, { rejectWithValue }) => {
    try {
      const supplier = await supplierService.create(supplierData);

      return {
        success: true,
        data: supplier,
        pagination: null,
        message: 'Supplier created successfully',
        timestamp: new Date().toISOString()
      } as ApiResponse<Supplier>;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentSuppliersSlice', 'createSupplier');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async (params: { id: string; supplierData: Partial<Supplier> }, { rejectWithValue }) => {
    try {
      const supplier = await supplierService.update(params.id, params.supplierData);

      return {
        success: true,
        data: supplier,
        pagination: null,
        message: 'Supplier updated successfully',
        timestamp: new Date().toISOString()
      } as ApiResponse<Supplier>;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentSuppliersSlice', 'updateSupplier');
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (id: string, { rejectWithValue }) => {
    try {
      await supplierService.remove(id);

      return {
        success: true,
        data: id,
        pagination: null,
        message: 'Supplier deleted successfully',
        timestamp: new Date().toISOString()
      } as ApiResponse<string>;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentSuppliersSlice', 'deleteSupplier');
      return rejectWithValue(errorMessage);
    }
  }
);

// State interface
interface ConsistentSuppliersState {
  suppliers: Supplier[];
  currentSupplier: Supplier | null;
  loading: boolean;
  error: string | null;
  success: boolean;
  pagination: PaginationInfo | null;
  message: string | null;
  cached: boolean;
  lastFetched: number | null;
  cacheTimeout: number;
}

const initialState: ConsistentSuppliersState = {
  suppliers: [],
  currentSupplier: null,
  loading: false,
  error: null,
  success: false,
  pagination: null,
  message: null,
  cached: false,
  lastFetched: null,
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
};

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setCurrentSupplier: (state, action: PayloadAction<Supplier | null>) => {
      state.currentSupplier = action.payload;
    },
    clearCurrentSupplier: (state) => {
      state.currentSupplier = null;
    },
    invalidateCache: (state) => {
      state.cached = false;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Suppliers
      .addCase(getSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getSuppliers.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<Supplier[]>;
        state.loading = false;
        state.suppliers = response.data || [];
        state.pagination = response.pagination || null;
        state.message = response.message || null;
        // Don't set success for GET operations - only for CRUD operations
        // state.success should remain unchanged
        state.cached = true;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(getSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Get Single Supplier
      .addCase(getSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSupplier.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<Supplier>;
        state.loading = false;
        state.currentSupplier = response.data || null;
        state.message = response.message || null;
      })
      .addCase(getSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create Supplier
      .addCase(createSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<Supplier>;
        state.loading = false;
        if (response.data) {
          state.suppliers.push(response.data);
        }
        state.message = response.message || null;
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Update Supplier
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<Supplier>;
        state.loading = false;
        if (response.data) {
          const index = state.suppliers.findIndex((s: Supplier) => s.supplier_id === response.data!.supplier_id);
          if (index !== -1) {
            state.suppliers[index] = response.data;
          }
          state.currentSupplier = response.data;
        }
        state.message = response.message || null;
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Delete Supplier
      .addCase(deleteSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<string>;
        state.loading = false;
        if (response.data) {
          state.suppliers = state.suppliers.filter((s: Supplier) => s.supplier_id !== response.data);
        }
        state.message = response.message || null;
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearMessage,
  setCurrentSupplier,
  clearCurrentSupplier,
  invalidateCache,
} = suppliersSlice.actions;

export default suppliersSlice.reducer;
